const SESSION_KEY = "feedbackSessionId";
const MAX_ROUTES = 20;
const MAX_REQUESTS = 30;
const MAX_CONSOLE = 20;
const MAX_MESSAGE = 500;

const routes = [];
const requests = [];
const consoleEvents = [];

let consoleCaptureInstalled = false;
let memorySessionId = "";

function pushCapped(list, item, max) {
  list.push(item);
  if (list.length > max) {
    list.splice(0, list.length - max);
  }
}

function nowIso() {
  return new Date().toISOString();
}

export function newId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Insecure contexts (http over a LAN address) have no randomUUID.
  const bytes = new Uint8Array(16);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function clip(value) {
  if (value == null) return "";
  const text = typeof value === "string" ? value : String(value);
  return text.length > MAX_MESSAGE ? text.slice(0, MAX_MESSAGE) : text;
}

function formatConsoleArgs(args) {
  return clip(
    args
      .map((arg) => {
        if (arg instanceof Error) {
          return arg.stack || arg.message;
        }
        if (typeof arg === "string") return arg;
        try {
          return JSON.stringify(arg);
        } catch (e) {
          return String(arg);
        }
      })
      .join(" ")
  );
}

export function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = newId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (e) {
    if (!memorySessionId) {
      memorySessionId = newId();
    }
    return memorySessionId;
  }
}

export function recordRoute(path, search) {
  pushCapped(
    routes,
    {
      path: path || "",
      search: search || "",
      at: nowIso(),
    },
    MAX_ROUTES
  );
}

export function recordRequest({ method, path, status, durationMs }) {
  pushCapped(
    requests,
    {
      method: method || "",
      path: path || "",
      status: Number.isFinite(status) ? status : 0,
      durationMs: Number.isFinite(durationMs) ? Math.round(durationMs) : 0,
      at: nowIso(),
    },
    MAX_REQUESTS
  );
}

function recordConsole(level, args) {
  pushCapped(
    consoleEvents,
    {
      level,
      message: formatConsoleArgs(args),
      at: nowIso(),
    },
    MAX_CONSOLE
  );
}

export function installConsoleCapture() {
  if (consoleCaptureInstalled || typeof window === "undefined") return;
  consoleCaptureInstalled = true;

  const originalError = console.error.bind(console);
  const originalWarn = console.warn.bind(console);

  console.error = (...args) => {
    recordConsole("error", args);
    originalError(...args);
  };
  console.warn = (...args) => {
    recordConsole("warn", args);
    originalWarn(...args);
  };

  window.addEventListener("error", (event) => {
    recordConsole("error", [event?.message || "window error"]);
  });
  window.addEventListener("unhandledrejection", (event) => {
    recordConsole("error", [event?.reason || "unhandled rejection"]);
  });
}

export function requestPath(config) {
  const base = String(config?.baseURL || "").replace(/\/+$/, "");
  const relative = String(config?.url || "").split("?")[0].replace(/^\/+/, "");
  const combined = relative ? `${base}/${relative}` : base;
  try {
    return new URL(combined).pathname;
  } catch (e) {
    return relative ? `/${relative}` : "";
  }
}

export function getSnapshot() {
  return {
    history: {
      routes: routes.map((item) => ({ ...item })),
      requests: requests.map((item) => ({ ...item })),
      console: consoleEvents.map((item) => ({ ...item })),
    },
  };
}
